import './LoginBox.scss';
import React, { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { getInitials } from '../../utils/utils';
import { useAuth } from '../../contexts/auth/AuthContext';
import { useTheme } from '../../contexts/theme/ThemeContext';
import { useAccount } from '../../contexts/account/AccountContexts';
import Loader from '../common/loader/Loader';
import { useTranslation } from 'react-i18next';
import { SETTINGS } from '../../routes/path';
import CustomTooltip from '../common/tooltip/CustomTooltip';
import { getUserAccounts } from '../../services/user/accounts/UserAccountsService';
import { changeActiveAccount } from '../../services/user/UserService';
import { UserAccountsResponse } from '../../models/user/accounts/UserAccountsResponse';
const LoginBox: React.FC = () => {
  const { logout } = useAuth();
  const { t } = useTranslation();
  const { theme, toggleTheme } = useTheme();
  const { accountResponse, refetchData } = useAccount();
  const [name, setName] = useState<string>('');
  const [userAccounts, setUserAccounts] = useState<UserAccountsResponse[] | null>(null);
  const [loadingAccounts, setLoadingAccounts] = useState<boolean>(false);
  const [switchingAccount, setSwitchingAccount] = useState<boolean>(false);

  useEffect(() => {
    if (!accountResponse) {
      return;
    }

    setName(accountResponse.firstName + ' ' + accountResponse.lastName);
  }, [accountResponse]);

  useEffect(() => {
    setLoadingAccounts(true);
    getUserAccounts()
      .then((accounts) => {
        setUserAccounts(accounts);
      })
      .catch((error) => {
        console.error('Error fetching user accounts:', error);
      })
      .finally(() => setLoadingAccounts(false));
  }, []);

  const handleAccountSwitch = async (accountId: number) => {
    if (switchingAccount || !accountResponse) {
      return;
    }

    if (accountId === accountResponse.accountId) {
      return;
    }

    try {
      setSwitchingAccount(true);
      await changeActiveAccount(accountId);
      refetchData();
      window.location.reload();
    } catch (error) {
      console.error('Error switching account:', error);
      setSwitchingAccount(false);
    }
  };

  if (!accountResponse) {
    return <Loader />;
  }

  return (
    <div className="login-box">
      <div className="login-box__content">
        <div className="login-box__avatar">{getInitials(name, accountResponse.email)}</div>
        <div className="login-box__name">{name.trim().length === 0 ? accountResponse.email : name}</div>
      </div>
      <div className="login-box__panel">
        {/* Account switching section */}
        {!loadingAccounts && userAccounts && userAccounts.length > 0 && (
          <div className="login-box__accounts-section">
            <div className="login-box__accounts-title">{t('yourAccounts')}</div>
            {userAccounts.map((account) => (
              <button
                key={account.accountId}
                onClick={() => handleAccountSwitch(account.accountId)}
                className={`login-box__account-item ${
                  account.accountId === accountResponse.accountId
                    ? 'login-box__account-item--active'
                    : ''
                }`}
                disabled={switchingAccount}
              >
                <div
                  className="login-box__account-avatar"
                  data-role={account.userRole.charAt(0)}
                  title={account.accountEmail}
                >
                  {getInitials(account.accountEmail, account.accountEmail)}
                </div>
                <div className="login-box__account-info">
                  <div className="login-box__account-name">{account.accountEmail}</div>
                  <div className="login-box__account-role">{account.userRole}</div>
                </div>
                {account.accountId === accountResponse.accountId && (
                  <i className="ico--check ico--sm login-box__account-check" />
                )}
                {switchingAccount && (
                  <div className="login-box__account-loading">
                    <Loader />
                  </div>
                )}
              </button>
            ))}
            <div className="login-box__divider" />
          </div>
        )}

        <NavLink
          className={({ isActive }) => (isActive ? 'login-box__item--active' : '') + ' login-box__item'}
          to={SETTINGS}
          id="settings-btn"
        >
          <i className="ico--settings ico--sm" />
          <span> {t('settings')}</span>
        </NavLink>
        <CustomTooltip
          tooltipText={t('settings')}
          targetRef="settings-btn"
          isMobileTooltip={true}
          direction="right"
        />
        <button onClick={toggleTheme} className="login-box__item login-box__item--theme" id="theme-toggle-btn">
          <span className={`login-box__theme-icon login-box__theme-icon--${theme}`} />
          <span>{theme === 'dark' ? t('lightMode') : t('darkMode')}</span>
        </button>
        <CustomTooltip
          tooltipText={theme === 'dark' ? t('lightMode') : t('darkMode')}
          targetRef="theme-toggle-btn"
          isMobileTooltip={true}
          direction="right"
        />
        <button onClick={logout} className="login-box__item" id="logout-btn">
          <i className="ico--logout ico--sm" />
          <span>{t('logout')}</span>
        </button>
        <CustomTooltip
          tooltipText={t('logout')}
          targetRef="logout-btn"
          isMobileTooltip={true}
          direction="right"
        />
      </div>
    </div>
  );
};

export default LoginBox;
